import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import RajyaGeetImg from "../assets/images/Navbar/RajyaGeet.jpg";
import { FaDownload, FaFilePdf, FaPlay, FaSearch, FaChevronDown } from "react-icons/fa";
import RajyaGeet from "../assets/documents/RajyaGeet.pdf";
import { setLanguage } from "../store/reducer.slice";

const AccessibilityHeader = ({
  fontSizeLevel,
  setFontSizeLevel,
  colorTheme,
  setColorTheme,
}) => {
  const adjustFontSize = (change) => {
    if (change === 0) {
      setFontSizeLevel(0);
    } else {
      setFontSizeLevel((prev) => Math.min(2, Math.max(-2, prev + change)));
    }
  };

  const dispatch = useDispatch();
  const selectedLang = useSelector((state) => state.language.selectedLang);
  const langRef = useRef(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const prevPath = useRef(location.pathname); // track the previous path
  const [isRajyageetOpen, setIsRajyageetOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Existing search useEffect
  useEffect(() => {
    const trimmed = query.trim();
    const timeout = setTimeout(() => {
        if (trimmed) {
            if (!location.pathname.startsWith("/search")) {
                prevPath.current = location.pathname;
            }
            navigate(`/search?query=${encodeURIComponent(trimmed)}`);
        }

        if (!trimmed && location.pathname.startsWith("/search")) {
            navigate(prevPath.current || "/");
        }
    }, 400);
    return () => clearTimeout(timeout);
}, [query, location.pathname, navigate]);

  const startVoiceSearch = () => {
    if (!recognitionRef.current) {
      alert(
        selectedLang === "मराठी"
          ? "आपल्या ब्राउझरमध्ये व्हॉइस सर्च समर्थित नाही"
          : "Voice search is not supported in this browser"
      );
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (err) {
      // prevents DOMException if clicked twice
    }
  };


  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = selectedLang === "मराठी" ? "mr-IN" : "en-IN";
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const spokenText = event.results[0][0].transcript;

      // 🔥 IMMEDIATE SEARCH
      if (spokenText.trim()) {
        navigate(`/search?query=${encodeURIComponent(spokenText)}`);
      }

      recognition.stop(); // stop mic immediately
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
  }, [selectedLang, navigate]);


  // New useEffect to clear query when not on /search
  useEffect(() => {
    if (!location.pathname.startsWith("/search")) {
      setQuery("");
    }
  }, [location.pathname]);

  const handleLangChange = (lang) => {
    dispatch(setLanguage(lang));
    setIsLangOpen(false);
  };

  const content = {
    English: {
      skipNav: "Skip to Navigation",
      skipContent: "Skip to Content",
      screenReader: "Screen Reader Access",
      fontSize: ["A-", "A", "A+"],
      theme: ["A", "A"],
      loginText: "LOG IN",
      searchPlaceholder: "Search",
    },
    मराठी: {
      skipNav: "दिशादर्शकाकडे जा",
      skipContent: "मुख्य विषयाकडे जा",
      screenReader: "स्क्रीन रीडर प्रवेश",
      fontSize: ["अ-", "अ", "अ+"],
      theme: ["अ", "अ"],
      loginText: "लॉग इन",
      searchPlaceholder: "शोधा",
    },
  };

  const t = content[selectedLang];

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("default", "black-theme");

    if (colorTheme === "black") {
      root.classList.add("black-theme");
    } else {
      root.classList.add("default");
    }
  }, [colorTheme]);



  const handleDownload = (type) => {
    switch (type) {
      case "image": {
        const linkImg = document.createElement("a");
        linkImg.href = RajyaGeetImg;
        linkImg.download = "RajyaGeet.jpg";
        linkImg.click();
        break;
      }

      case "pdf": {
        const linkPdf = document.createElement("a");
        linkPdf.href = RajyaGeet;
        linkPdf.download = "RajyaGeet.pdf";
        linkPdf.click();
        break;
      }

      case "audio":
        window.open(
          "https://cultural.maharashtra.gov.in/Images/RajyaGeet.wav",
          "_blank"
        );
        break;

      default:
        break;
    }
  };


  return (
    <div className="w-full bg-white px-2 sm:px-4  lg:px-17 py-2 flex flex-wrap justify-center sm:justify-end md:justify-center items-center text-xs sm:text-sm font-medium text-gray-700 gap-2 sm:gap-8">
      {/* Accessibility Links */}
      <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-3 text-[10px] xs:text-xs sm:text-sm">
        <button
          onClick={() => setIsRajyageetOpen(true)}
          className="px-2 py-1 sm:px-3 sm:py-1 text-xs sm:text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition"
        >
          {selectedLang === "मराठी" ? "राज्यगीत" : "Rajyageet"}
        </button>
        {isRajyageetOpen && (
          <div
            className="fixed inset-0 z-[2000] flex items-start justify-center overflow-auto bg-black bg-opacity-50 px-2 py-4"
            role="button"
            tabIndex={0}
            onClick={() => setIsRajyageetOpen(false)}
            onKeyDown={(e) => ["Enter", " "].includes(e.key) && setIsRajyageetOpen(false)}
          >
            <div
              className="bg-white w-full max-w-xl rounded-lg shadow-lg p-6 relative"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                {/* Text */}
                <h2 className="text-2xl sm:text-3xl font-semibold w-full sm:w-auto text-blue-800">
                  Download Rajyageet -
                </h2>

                {/* Buttons */}
                <div className="flex w-full sm:w-auto justify-start sm:justify-start gap-2">
                  {/* Image Download */}
                  <button
                    onClick={() => handleDownload("image")}
                    title="Download Image"
                    className="px-2 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 transition text-base"
                  >
                    <FaDownload />
                  </button>

                  {/* PDF Download */}
                  <button
                    onClick={() => handleDownload("pdf")}
                    title="Download PDF"
                    className="px-2 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 transition text-base"
                  >
                    <FaFilePdf />
                  </button>

                  {/* Audio Play */}
                  <button
                    onClick={() => handleDownload("audio")}
                    title="Play Audio"
                    className="px-2 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 transition text-base"
                  >
                    <FaPlay />
                  </button>
                </div>
              </div>


              {/* Image */}
              <div className="flex justify-center mb-6">
                <img
                  src={RajyaGeetImg}
                  alt="Rajygeet"
                  className="w-full max-w-full max-h-[90vh] object-contain rounded"
                />
              </div>

              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setIsRajyageetOpen(false)}
                  className="px-4 py-1.5 sm:px-6 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm sm:text-lg transition"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        )}

        <a href="#main-nav" className="hover:underline whitespace-nowrap">
          {t.skipNav}
        </a>
        <span className="mx-1">|</span>
        <a href="#main-content" className="hover:underline whitespace-nowrap">
          {t.skipContent}
        </a>
        <span className="mx-1">|</span>
        <Link to="/screenreader" className="hover:underline whitespace-nowrap">
          {t.screenReader}
        </Link>
      </div>


      {/* Font Size & Theme Controls */}
      <div className="flex items-center gap-4 sm:gap-8 flex-wrap justify-center">
        {/* Font Size Spans */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => adjustFontSize(-1)}
            className={`allow-bg-yellowww cursor-pointer border px-1.5 py-0.5 rounded text-xs sm:text-sm ${fontSizeLevel === -1 ? "bg-orange-500 text-white" : "bg-white"
              }`}
          >
            {t.fontSize[0]}
          </button>
          <button
            onClick={() => adjustFontSize(0)}
            className={`allow-bg-yellowww allow-bg-orange cursor-pointer border px-1.5 py-0.5 rounded text-xs sm:text-sm ${fontSizeLevel === 0 ? "bg-orange-500 text-white" : "bg-white"
              }`}
          >
            {t.fontSize[1]}
          </button>
          <button
            onClick={() => adjustFontSize(1)}
            className={`allow-bg-yellowww cursor-pointer border px-1.5 py-0.5 rounded text-xs sm:text-sm ${fontSizeLevel === 1 ? "bg-orange-500 text-white" : "bg-white"
              }`}
          >
            {t.fontSize[2]}
          </button>
        </div>

        {/* Theme Switcher Spans */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setColorTheme("default")}
            title="Default Theme"
            className={`allow-bg-white bg-white cursor-pointer border px-1.5 py-0.5 rounded text-xs sm:text-sm transition 
    ${colorTheme === "default" ? "ring-2 ring-black shadow-sm bg-white text-black" : "border-gray-300 bg-black text-yellow-300"}
    hover:ring-2 hover:ring-black focus:outline-none`}
          >
            {t.theme[0]}
          </button>

          <button
            onClick={() => setColorTheme("black")}
            title="Black Theme"
            className={`allow-bg-yellowww cursor-pointer border px-1.5 py-0.5 rounded text-xs sm:text-sm transition 
              ${colorTheme === "black" ? "ring-2 ring-yellow-300 shadow-sm bg-black text-white" : "border-gray-300 bg-black text-white"}
              hover:ring-2 hover:ring-yellow-300 focus:outline-none`}
          >
            {t.theme[0]}
          </button>
        </div>



        <div className="flex items-center gap-1 sm:gap-3">
          {/* Search Box */}
          <div className="flex items-center border rounded px-2 py-1 w-32 sm:w-48">
            <FaSearch className="text-gray-500 mr-1 shrink-0" />
            <span className="text-gray-400 text-sm mr-1">|</span>
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              className="w-full outline-none text-xs sm:text-sm bg-transparent"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {/* Login */}
          <Link to="/login">
            <button
              className="border px-3 py-1 rounded h-7 sm:h-[30px] text-[10px] sm:text-sm shrink-0 flex items-center justify-center 
             hover:bg-orange-600 hover:text-white transition whitespace-nowrap"
            >
              {t.loginText}
            </button>


          </Link>
          {/* Language Dropdown */}
          <div className="relative inline-block text-left z-[1000] shrink-0" ref={langRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="text-sm border px-2 py-1 rounded flex items-center gap-1 w-20 justify-between"
            >
              {selectedLang}
              <FaChevronDown className="text-xs" />
            </button>

            {isLangOpen && (
              <div className="absolute right-0 mt-1 w-28 bg-white border rounded shadow-md z-[9999]">
                {["English", "मराठी"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLangChange(lang)}
                    className="block w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    {lang}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Voice Search Icon */}
         <button
  type="button"
  onClick={startVoiceSearch}
  title={selectedLang === "मराठी" ? "आवाजातून शोधा" : "Voice Search"}
  className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-full border shrink-0
    ${
      isListening
        ? "bg-blue-500 text-white animate-pulse"
        : "bg-orange-500 text-white hover:bg-orange-600"
    }
  `}
>
  🎤
  {/* 🎤 Mic Icon (always visible) */}
  {/* <FaMicrophone className="text-sm" /> */}

  {/* Text → hidden on small screens */}
  <span className="hidden sm:inline text-xs font-semibold whitespace-nowrap">
    {selectedLang === "मराठी" ? "आवाजातून शोधा" : "Voice Search"}
  </span>
</button>



        </div>

      </div>

    </div>
  );
};

AccessibilityHeader.propTypes = {
  fontSizeLevel: PropTypes.number.isRequired,
  setFontSizeLevel: PropTypes.func.isRequired,
  colorTheme: PropTypes.string.isRequired,
  setColorTheme: PropTypes.func.isRequired,
};

export default AccessibilityHeader;
